/**
 * Board Selector Popup
 *
 * Displays all available boards in the organization in an accordion view, categorized by:
 * 1. My Boards - User's own boards + group boards (group boards show a "Group" badge)
 * 2. Other Boards - All other boards in the organization
 *
 * Each board item shows the creator's display name as a subtle watermark,
 * clickable to navigate to their profile via deep link.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useAuth } from '@/hooks/useAuth';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { useModalManager } from '@/hooks/useModalManager';
import { ModalId } from '@/types/modal-manager.types';
// eslint-disable-next-line no-restricted-imports -- direct call intentional: GET_ALL_BOARDS is a silent background fetch for board list dropdown
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import type {
  GetAllBoardsInput,
  GetAllBoardsOutput,
  BoardListItem,
} from '@/types/board-discovery.types';
import { Icon, IconName } from '@/icons';
import { Button } from '@/components/ui/button';
import { useThemeColors } from '@/styles';
import { sizing, focusRing, typography, radius } from '@/styles/design-tokens';
import { logger } from '@/utils/logger';
import { generateDeepLink, DeepLinkEntity } from '@/utils/deep-linking';
import { useUserMap, getUserDisplayName } from '@/hooks/queries';
import CreateBoardForm from './CreateBoardForm';
import CreateBoardFromTemplateForm from './CreateBoardFromTemplateForm';
import './BoardSelectorPopup.css';

interface BoardSelectorPopupProps {
  isOpen: boolean;
  onClose: () => void;
  currentBoardId?: string;
  onBoardSelect: (boardId: string) => void;
}

type AccordionSection = 'myBoards' | 'otherBoards';

interface BoardItemWithGroup extends BoardListItem {
  isGroup?: boolean;
}

/** Tag display config - title case labels and semantic colors */
const getTagDisplay = (
  colors: ReturnType<typeof useThemeColors>
): Record<string, { label: string; color: string }> => ({
  primary: { label: 'Primary', color: colors.purple },
  secondary: { label: 'Secondary', color: colors.teal },
  personal: { label: 'Personal', color: colors.coral },
});

export default function BoardSelectorPopup({
  isOpen,
  onClose,
  currentBoardId,
  onBoardSelect,
}: BoardSelectorPopupProps) {
  const { user } = useAuth();
  const { activeOrganizationId } = useActiveOrganization();
  const colors = useThemeColors();
  const TAG_DISPLAY = getTagDisplay(colors);
  const { openModal, closeModal } = useModalManager();
  const { userMap } = useUserMap(activeOrganizationId);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [boardData, setBoardData] = useState<GetAllBoardsOutput | null>(null);
  const [openSection, setOpenSection] = useState<AccordionSection>('myBoards');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Board creation flow state
  const [showCreateBoardForm, setShowCreateBoardForm] = useState(false);
  const [showCreateFromTemplate, setShowCreateFromTemplate] = useState(false);

  // Track modal open/close state
  useEffect(() => {
    if (isOpen) {
      openModal(ModalId.BOARD_SELECTOR);
    } else {
      closeModal(ModalId.BOARD_SELECTOR);
    }
  }, [isOpen, openModal, closeModal]);

  // Debounce search query for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 150);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchBoards = useCallback(async () => {
    logger.debug('fetchBoards called', {
      userId: user?.id,
      organizationId: user?.organization_id,
    });

    if (!user?.id || !user?.organization_id) {
      logger.error('Missing user.id or user.organization_id');
      setError('Unable to load boards. Please log in again.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // organization_id is auto-injected by ActionOrchestrator before validation
      const result = await actionOrchestrator.execute<
        GetAllBoardsInput,
        GetAllBoardsOutput
      >(Action.GET_ALL_BOARDS, {
        user_id: user.id,
      } as GetAllBoardsInput);

      logger.debug('GET_ALL_BOARDS result', {
        code: result.code,
        data: result.data,
        boards: result.data?.boards,
      });

      if (result.code === ResponseCode.SUCCESS && result.data) {
        logger.debug('Setting boardData', { data: result.data });
        setBoardData(result.data);
      } else {
        setError('Failed to load boards. Please try again.');
        logger.error('Failed to fetch boards', { code: result.code });
      }
    } catch (err) {
      setError('Failed to load boards. Please try again.');
      logger.error('Error fetching boards', { error: err });
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, user?.organization_id]);

  // Fetch boards when popup opens
  useEffect(() => {
    if (isOpen && user?.id && user?.organization_id) {
      fetchBoards();
    }
  }, [isOpen, user?.id, user?.organization_id, fetchBoards]);

  const handleBoardClick = useCallback(
    (boardId: string) => {
      // Don't navigate if clicking the currently active board
      if (boardId === currentBoardId) {
        onClose();
        return;
      }

      onBoardSelect(boardId);
      onClose();
    },
    [currentBoardId, onClose, onBoardSelect]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleCreateNewBoard = useCallback(() => {
    setShowCreateBoardForm(true);
  }, []);

  const handleBoardCreated = useCallback(
    (boardId: string) => {
      logger.info('Board created successfully', { board_id: boardId });

      // Refresh board list
      fetchBoards();

      // Navigate to the new board
      onBoardSelect(boardId);
      onClose();
    },
    [onBoardSelect, onClose, fetchBoards]
  );

  // Sort boards: primary → secondary → other tags → grouped (no tag) → non-tagged
  const sortBoardsByTagPriority = useCallback(
    (boards: BoardItemWithGroup[]): BoardItemWithGroup[] => {
      const tagRank = (b: BoardItemWithGroup): number => {
        if (b.tag === 'primary') return 0;
        if (b.tag === 'secondary') return 1;
        if (b.tag) return 2;
        if (b.isGroup) return 3;
        return 4;
      };
      return [...boards].sort((a, b) => {
        const rankDiff = tagRank(a) - tagRank(b);
        return rankDiff !== 0 ? rankDiff : a.title.localeCompare(b.title);
      });
    },
    []
  );

  // Memoized filter function
  const filterBoards = useCallback(
    (boards: BoardItemWithGroup[], query: string) => {
      const filtered = query.trim()
        ? boards.filter(board =>
            board.title.toLowerCase().includes(query.toLowerCase())
          )
        : [...boards];
      return sortBoardsByTagPriority(filtered);
    },
    [sortBoardsByTagPriority]
  );

  // Combine my_boards + group_boards and sort by tag priority
  const filteredMyBoards = useMemo(() => {
    const ownBoards: BoardItemWithGroup[] = (
      boardData?.boards.my_boards || []
    ).map(b => ({ ...b, isGroup: false }));
    const groupBoards: BoardItemWithGroup[] = (
      boardData?.boards.group_boards || []
    ).map(b => ({ ...b, isGroup: true }));
    const query = debouncedQuery.toLowerCase().trim();
    const filterFn = (b: BoardItemWithGroup) =>
      !query || b.title.toLowerCase().includes(query);
    return sortBoardsByTagPriority(
      [...ownBoards, ...groupBoards].filter(filterFn)
    );
  }, [
    boardData?.boards.my_boards,
    boardData?.boards.group_boards,
    debouncedQuery,
    sortBoardsByTagPriority,
  ]);

  const filteredOtherBoards = useMemo(
    () => filterBoards(boardData?.boards.other_boards || [], debouncedQuery),
    [boardData?.boards.other_boards, debouncedQuery, filterBoards]
  );

  const handleCreatorClick = useCallback(
    (e: React.MouseEvent, ownerId: string) => {
      e.stopPropagation();
      const profileUrl = generateDeepLink(DeepLinkEntity.USER, ownerId);
      window.open(profileUrl, '_blank', 'noopener,noreferrer');
    },
    []
  );

  const renderBoardItem = useCallback(
    (board: BoardItemWithGroup) => {
      const isActive = board.id === currentBoardId;
      const ownerName = getUserDisplayName(userMap, board.owner_id);

      return (
        <button
          onClick={() => handleBoardClick(board.id)}
          className={`board-item ${isActive ? 'active' : ''}`}
          style={
            {
              '--active-bg': colors.active.background,
              '--active-text': colors.active.text,
              '--hover-bg': colors.hover.background,
              '--text-primary': colors.text.primary,
            } as React.CSSProperties
          }
        >
          <div className="flex-1 min-w-0 relative">
            <div className="font-semibold truncate text-base">
              {board.title}
            </div>
            {ownerName && (
              <span
                className="board-item-creator"
                style={{
                  color: isActive ? colors.active.text : colors.text.secondary,
                }}
                onClick={
                  board.owner_id
                    ? e => handleCreatorClick(e, board.owner_id!)
                    : undefined
                }
                role={board.owner_id ? 'link' : undefined}
                tabIndex={board.owner_id ? 0 : undefined}
              >
                {ownerName}
              </span>
            )}
          </div>
          {board.tag && TAG_DISPLAY[board.tag] && (
            <span
              className="board-item-tag-badge"
              style={{
                backgroundColor: TAG_DISPLAY[board.tag].color + '20',
                color: TAG_DISPLAY[board.tag].color,
                border: `1px solid ${TAG_DISPLAY[board.tag].color}40`,
                padding: '0.0625rem 0.375rem',
                borderRadius: radius.full,
                fontSize: '0.7rem',
                fontWeight: 500,
              }}
            >
              {TAG_DISPLAY[board.tag].label}
            </span>
          )}
          {board.isGroup && (
            <span
              className="board-item-group-badge"
              style={{
                backgroundColor: colors.blue,
                color: colors.text.inverse,
                padding: '0.0625rem 0.375rem',
                borderRadius: radius.full,
              }}
            >
              Group
            </span>
          )}
          {isActive && (
            <Icon
              name={IconName.CHECK}
              size="sm"
              style={{ color: colors.active.text }}
            />
          )}
        </button>
      );
    },
    [currentBoardId, handleBoardClick, handleCreatorClick, colors, userMap]
  );

  const renderAccordionSection = useCallback(
    (
      sectionKey: AccordionSection,
      title: string,
      boards: BoardListItem[],
      emptyMessage: string
    ) => {
      const isOpen = openSection === sectionKey;
      const boardCount = boards.length;

      logger.debug('Rendering accordion section', {
        title,
        boardCount,
        boards,
      });

      return (
        <Collapsible
          open={isOpen}
          onOpenChange={open => setOpenSection(open ? sectionKey : 'myBoards')}
          className="mb-2"
        >
          <CollapsibleTrigger
            className={`accordion-trigger ${isOpen ? 'open' : ''}`}
            style={
              {
                '--hover-bg': colors.hover.background,
                '--text-primary': colors.text.primary,
                '--text-secondary': colors.text.secondary,
                '--border-default': colors.border.default,
              } as React.CSSProperties
            }
          >
            <div className="flex items-center gap-2">
              <Icon
                name={isOpen ? IconName.CHEVRON_DOWN : IconName.CHEVRON_RIGHT}
                size="sm"
                style={{ color: colors.text.secondary }}
              />
              <h3 className="font-semibold text-base">{title}</h3>
              <span
                className="text-sm px-2 py-0.5 rounded-full font-medium"
                style={{
                  backgroundColor: colors.primary,
                  color: colors.text.inverse,
                  fontSize: typography.sizes.sm,
                  lineHeight: typography.lineHeights.tight,
                  minWidth: 'calc(24px * var(--sizing-multiplier, 1))',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {boardCount}
              </span>
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent className="accordion-content">
            <div>
              {boards.length === 0 ? (
                <div
                  className="px-4 py-3 text-center italic text-sm"
                  style={{
                    color: colors.text.secondary,
                  }}
                >
                  {emptyMessage}
                </div>
              ) : (
                <div className="board-list">
                  {boards.map(board => (
                    <React.Fragment key={board.id}>
                      {renderBoardItem(board)}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      );
    },
    [openSection, colors, renderBoardItem]
  );

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className="max-w-2xl flex flex-col"
          style={{
            height: 'min(calc(600px * var(--sizing-multiplier, 1)), 90vh)',
          }}
          onKeyDown={handleKeyDown}
        >
          {/* Content area with fixed height */}
          <div className="flex-1 overflow-y-auto px-2 -mx-2 min-h-0">
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div
                  className="animate-spin rounded-full border-4 border-t-transparent"
                  style={{
                    width: sizing.icon.lg,
                    height: sizing.icon.lg,
                    borderColor: colors.border.default,
                    borderTopColor: 'transparent',
                  }}
                />
                <span className="ml-3" style={{ color: colors.text.secondary }}>
                  Loading boards...
                </span>
              </div>
            )}

            {error && (
              <div
                className="px-4 py-3 rounded-lg mb-4 border"
                style={{
                  backgroundColor: colors.error + '20',
                  borderColor: colors.error + '40',
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon
                    name={IconName.ALERT_CIRCLE}
                    size="sm"
                    style={{ color: colors.error }}
                  />
                  <span
                    className="font-semibold"
                    style={{ color: colors.error }}
                  >
                    Error
                  </span>
                </div>
                <p className="text-sm" style={{ color: colors.error }}>
                  {error}
                </p>
                <Button onClick={fetchBoards} size="sm" className="mt-3">
                  Retry
                </Button>
              </div>
            )}

            {!isLoading && !error && boardData && (
              <div className="space-y-4">
                {/* Create Board Buttons */}
                <div className="px-2 flex gap-2">
                  <Button
                    onClick={handleCreateNewBoard}
                    size="lg"
                    className="flex-1 gap-2"
                  >
                    <Icon name={IconName.PLUS} size="sm" />
                    New Board
                  </Button>
                  <Button
                    onClick={() => setShowCreateFromTemplate(true)}
                    variant="outline"
                    size="lg"
                    className="flex-1 gap-2"
                  >
                    <Icon name={IconName.LAYOUT_TEMPLATE} size="sm" />
                    From Template
                  </Button>
                </div>

                {/* Search Input */}
                <div className="px-2">
                  <input
                    type="text"
                    placeholder="Search boards..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="search-input"
                    style={
                      {
                        '--bg-offwhite': colors.offWhite,
                        '--border-default': colors.border.default,
                        '--text-primary': colors.text.primary,
                        '--primary': colors.primary,
                        '--focus-offset': focusRing.offset,
                        '--focus-width': focusRing.width,
                      } as React.CSSProperties
                    }
                  />
                </div>

                {/* Accordion Sections */}
                <div className="space-y-2">
                  {renderAccordionSection(
                    'myBoards',
                    'My Boards',
                    filteredMyBoards,
                    debouncedQuery.trim()
                      ? 'No boards found'
                      : 'No boards available'
                  )}

                  {renderAccordionSection(
                    'otherBoards',
                    'Other Boards',
                    filteredOtherBoards,
                    debouncedQuery.trim()
                      ? 'No boards found'
                      : 'No other boards available'
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Board Form Modal */}
      <CreateBoardForm
        isOpen={showCreateBoardForm}
        onClose={() => setShowCreateBoardForm(false)}
        onSuccess={handleBoardCreated}
      />

      {/* Create Board from Template Modal */}
      <CreateBoardFromTemplateForm
        isOpen={showCreateFromTemplate}
        onClose={() => setShowCreateFromTemplate(false)}
        onSuccess={handleBoardCreated}
      />
    </>
  );
}
